import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Button, Divider, Checkbox, FormControlLabel } from '@mui/material';
import { InstitutionSettingsService } from '../../services/institutionSettings.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

const InstitutionSettingsPage: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution();
    const [loading, setLoading] = useState(true);
    const [twoFactorPolicy, setTwoFactorPolicy] = useState<'OPTIONAL' | 'REQUIRED'>('OPTIONAL');
    const [savingTwoFactor, setSavingTwoFactor] = useState(false);

    useEffect(() => {
        const load = async () => {
            if (!institutionId) {
                setLoading(false);
                return;
            }
            try {
                const settings = await InstitutionSettingsService.getSettings(institutionId);
                if (settings?.twoFactorPolicy) {
                    setTwoFactorPolicy(settings.twoFactorPolicy);
                }
            } catch (error) {
                toast.showApiError(error, 'Failed to load institution settings');
            } finally {
                setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const handleSaveTwoFactorPolicy = async () => {
        if (!institutionId) {
            toast.showError('No institution selected');
            return;
        }
        try {
            setSavingTwoFactor(true);
            await InstitutionSettingsService.updateTwoFactorPolicy(institutionId, twoFactorPolicy);
            toast.showSuccess('Two-factor authentication policy saved');
        } catch (error) {
            toast.showApiError(error, 'Failed to save two-factor authentication policy');
        } finally {
            setSavingTwoFactor(false);
        }
    };

    if (loading) {
        return <Box sx={{ p: 3 }}><Typography>Loading...</Typography></Box>;
    }

    if (!institutionId) {
        return <Box sx={{ p: 3 }}><Typography>No institution selected</Typography></Box>;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>Institution Settings</Typography>

            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Two-Factor Authentication</Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    When required, every user in this institution must set up an authenticator app before they can log in.
                </Typography>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={twoFactorPolicy === 'REQUIRED'}
                            onChange={(e) => setTwoFactorPolicy(e.target.checked ? 'REQUIRED' : 'OPTIONAL')}
                        />
                    }
                    label="Require two-factor authentication for all users"
                    sx={{ mb: 2 }}
                />

                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveTwoFactorPolicy}
                        disabled={savingTwoFactor}
                    >
                        {savingTwoFactor ? 'Saving...' : 'Save Two-Factor Settings'}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default InstitutionSettingsPage;
